<template>
  <!--
    The Design control (AppList Designer v2, E1–E3): a workspace admin either lets GNey design the
    listing card or customizes it, from the detail view's wand. The segmented mode IS the origin
    stamp — a picked candidate applies as origin "ai", a saved row layout as origin "user" — and a
    person's card is never overwritten by the platform (D3). Switching Customize → GNey parks the
    manual card (appListUserBackup, D12) and "Restore" brings it back. Previews render real
    entries through the shared FTSAppListCard (D11), the hint steers the generator (D10).
    Customize carries the app listing designer's layout block too — layout, grid size, icon field,
    icon size and the Poster switch — over the two layouts the web can draw; the app's own layouts
    show read-only.
    GNey mode takes the same layout choice as a pin: unlike the hint it is a constraint, enforced
    in the tool schema server-side, so a picked layout is the layout every candidate comes back on.
  -->
  <fts-popper append-to-body placement="bottom-start" content-class="listing-designer-dropdown" @show="onShow" ref="popper">
    <template #content>
      <div class="ld-header">
        <div class="ld-header__title">
          <font-awesome-icon :icon="['fas', 'wand-magic-sparkles']" :color="`var(--fts-ai)`" />
          <span>Card design</span>
          <span class="ld-header__listing" v-if="design && design.name">· {{ design.name }}</span>
        </div>
        <div class="ld-origin" :class="`ld-origin--${originKind}`" v-if="!loading">
          <span v-if="applying || generating">⏳ {{ applying ? 'Applying…' : 'Generating…' }}</span>
          <span v-else>{{ originLabel }}</span>
        </div>
      </div>

      <div class="ld-loading" v-if="loading"><fts-spinner :size="16" show /> Loading the card…</div>
      <div class="ld-error" v-if="error">{{ error }}</div>

      <template v-if="!loading && design">
        <!-- segmented mode -->
        <div class="ld-seg" role="tablist">
          <div class="ld-seg__item" :class="{ 'ld-seg__item--on ld-seg__item--ai': mode === 'ai' }" role="tab" @click="setMode('ai')">
            <font-awesome-icon :icon="['fas', 'wand-magic-sparkles']" /> GNey AI
          </div>
          <div class="ld-seg__item" :class="{ 'ld-seg__item--on ld-seg__item--user': mode === 'custom' }" role="tab" @click="setMode('custom')">
            <font-awesome-icon :icon="['fas', 'pen-ruler']" /> Customize
          </div>
        </div>

        <!-- AI mode -->
        <div class="ld-section" v-if="mode === 'ai'">
          <!--
            Pinning a layout is a constraint, not a wish: the server builds the tool schema with
            that one "type", so the model cannot drift off it the way it can off the hint. Grid is
            offered only when an image field exists — a grid without a photo passes the guard but
            renders with no lead, which is not a card anyone asked for.
          -->
          <div class="ld-pin">
            <span class="ld-pin__label">Layout</span>
            <div class="ld-choice">
              <button v-for="p in layoutPins" :key="p.value === null ? 'any' : p.value" class="ld-choice__opt"
                :class="{ 'ld-choice__opt--on': aiLayout === p.value }" :disabled="generating"
                @click="aiLayout = p.value">{{ p.label }}</button>
            </div>
          </div>

          <div class="ld-hint">
            <input type="text" class="ld-hint__input" v-model="hint" placeholder="Tell GNey what matters — “photo first, price big”"
              @keydown.enter="generate" :disabled="generating" />
            <button class="ld-btn ld-btn--ai" @click="generate" :disabled="generating">
              <fts-spinner :size="12" show v-if="generating" />
              <template v-else>Generate</template>
            </button>
          </div>

          <div class="ld-candidates" v-if="candidates.length">
            <div v-for="c in candidates" :key="c.key" class="ld-candidate" :class="{ 'ld-candidate--selected': selectedKey === c.key }"
              @click="selectedKey = c.key">
              <div class="ld-candidate__pick">
                <span class="ld-radio" :class="{ 'ld-radio--on': selectedKey === c.key }"></span>
                <span class="ld-candidate__key">{{ c.key }}</span>
                <span class="ld-candidate__meta">{{ describeShell(c.appList) }}<template v-if="c.turns > 1"> · repaired</template></span>
              </div>
              <div class="ld-candidate__preview stopPropagation">
                <div class="ld-preview__card" :style="previewStyle">
                  <fts-app-list-card :item="previewItem" :appList="c.appList" :columns="columns" :states="allStates"
                    :activities="allActivities" :vectorId="moduleId" :moduleId="moduleId" />
                </div>
              </div>
            </div>
          </div>
          <div class="ld-empty" v-else-if="!generating">
            <template v-if="design.appList">Generate up to three candidates and pick one, or keep the card as it is.</template>
            <template v-else>This listing has no card yet — generate one.</template>
          </div>

          <div class="ld-foot">
            <div class="ld-foot__note">
              Applies for everyone in this workspace. Previews use your real entries.
              <template v-if="design.backup"><br />Your customized card is kept — <a href="#" @click.prevent="restoreBackup">restore it</a>.</template>
            </div>
            <button class="ld-btn ld-btn--ai" :disabled="!selectedCandidate || applying" @click="applyCandidate">Apply design</button>
          </div>
        </div>

        <!-- Customize mode -->
        <div class="ld-section" v-else>
          <!--
            The card-level shell, the app listing designer's layout block: which layout, how big a
            grid tile, which field is the icon and how big. Only Detail and Grid are offered — they
            are the two every renderer knows and the only two this list can show. A card the app
            put on one of its own layouts says so and keeps it (Save passes it through untouched);
            changing it belongs on the surface that can actually draw it.
          -->
          <div class="ld-settings">
            <div class="settings-item">
              <font-awesome-icon :icon="['fas', 'table-cells-large']" />
              <span>Layout</span>
              <div class="ld-choice" v-if="!isAppLayout">
                <button v-for="t in layoutTypes" :key="t.value" class="ld-choice__opt"
                  :class="{ 'ld-choice__opt--on': layout.type === t.value }" @click="setLayoutType(t.value)">{{ t.label }}</button>
              </div>
              <span class="ld-app-layout" v-else>{{ appLayoutLabel }} <span class="ld-app-layout__note">· set in the app</span></span>
            </div>

            <div class="settings-item" v-if="layout.type === 'grid'">
              <font-awesome-icon :icon="['fas', 'expand']" />
              <span>Card size</span>
              <div class="ld-choice">
                <button v-for="s in gridSizes" :key="s" class="ld-choice__opt"
                  :class="{ 'ld-choice__opt--on': layout.size === s }" @click="setGridSize(s)">{{ s }}</button>
              </div>
            </div>

            <fts-icon-field-dropdown v-if="layout.type === 'detail'" :informations="iconFields"
              :initial-selected="layout.icon && layout.icon.informationId" @select="setIconField">
              <template #toggle>
                <div class="settings-item">
                  <font-awesome-icon :icon="['fas', 'image']" />
                  <span>Icon</span>
                  <button class="edit-button">{{ iconFieldName }} <font-awesome-icon :icon="['fas', 'chevron-right']" width="10px" /></button>
                </div>
              </template>
            </fts-icon-field-dropdown>

            <div class="settings-item" v-if="layout.type === 'detail' && layout.icon">
              <font-awesome-icon :icon="['fas', 'expand']" />
              <span>Icon size</span>
              <div class="ld-choice">
                <button v-for="s in iconSizes" :key="s" class="ld-choice__opt"
                  :class="{ 'ld-choice__opt--on': layout.icon.size === s }" @click="setIconSize(s)">{{ s }}</button>
              </div>
            </div>

            <!-- The app designer's Poster switch (appList.overlay): the photo fills the card and the
                 other rows sit over it. Offered once the card has a photo row, on a layout that can
                 take the tile (not Detail, not the app's Masonry). -->
            <div class="settings-item" v-if="posterAvailable">
              <font-awesome-icon :icon="['fas', 'image']" />
              <span>Poster</span>
              <span class="ld-poster__hint">Fields over the photo</span>
              <div class="ld-choice">
                <button class="ld-choice__opt" :class="{ 'ld-choice__opt--on': !layout.overlay }" @click="setOverlay(false)">Off</button>
                <button class="ld-choice__opt" :class="{ 'ld-choice__opt--on': layout.overlay }" @click="setOverlay(true)">On</button>
              </div>
            </div>
          </div>

          <div class="card-section-wrapper">
            <div class="card-column">
              <div v-for="(row, rowIndex) in rows" :key="rowIndex">
                <fts-add-row-dropdown :invalid-options="getInvalidItems(rowIndex)" :informations="editableInformations"
                  :activities="listableActivities" :initial-selected="rows[rowIndex].items" @select="(ids) => onSelectOption(ids, rowIndex)">
                  <template #toggle="{ open }">
                    <div class="card-row" :class="{ 'default-card': isDefaultCardRow(row) || isImageCardRow(row) }">
                      <div @click.stop>
                        <fts-designer-row-menu-dropdown :index="rowIndex" :totalLength="rows.length"
                          @select="(operation) => onRowRearrange(rowIndex, operation)">
                          <template #toggle="{ open }">
                            <font-awesome-icon class="drag-icon" :icon="['fas', 'bars']" :width="10" />
                          </template>
                        </fts-designer-row-menu-dropdown>
                      </div>
                      <div v-for="(cell, i) in row.items" :key="i" class="row-cell"
                        :class="isDefaultCardRow(row) || isImageCardRow(row) ? 'align-center' : getCellAlignClass(row.items.length, i)">
                        <div v-if="cell.id === 'defaultCard'">Default card</div>
                        <div v-else-if="cell.id === 'state'">State</div>
                        <div v-else-if="isImageCardRow(row)"><font-awesome-icon :icon="['fas', 'image']" /></div>
                        <div v-else-if="cell.type === 'information'" :class="cellClass(cell)">{{ getInformationName(cell) }}</div>
                        <div v-else-if="cell.type === 'activity'"><div class="activity-cell">{{ getActivityName(cell) }}</div></div>
                      </div>
                    </div>
                  </template>
                </fts-add-row-dropdown>
              </div>

              <div class="add-row-wrapper">
                <fts-add-row-dropdown :invalid-options="getInvalidItems()" :informations="editableInformations"
                  :activities="listableActivities" @select="(ids) => onSelectOption(ids, null)">
                  <template #toggle="{ open }">
                    <button class="add-row-button"><font-awesome-icon :icon="['fas', 'plus']" /> Row</button>
                  </template>
                </fts-add-row-dropdown>
              </div>
            </div>
          </div>

          <fts-default-action-dropdown :activities="listableActivities" :default-activities="defaultActivities"
            :initialSelected="action" @select="onSelectAction">
            <template #toggle="{ open }">
              <div class="settings-item">
                <font-awesome-icon :icon="['fas', 'arrow-pointer']" />
                <span>Default action</span>
                <button class="edit-button">{{ getActionName(action) }} <font-awesome-icon :icon="['fas', 'chevron-right']" width="10px" /></button>
              </div>
            </template>
          </fts-default-action-dropdown>

          <div class="ld-preview" v-if="rows.length">
            <div class="ld-preview__cap">Preview</div>
            <div class="stopPropagation">
              <div class="ld-preview__card" :style="previewStyle">
                <fts-app-list-card :item="previewItem" :appList="customAppList" :columns="columns" :states="allStates"
                  :activities="allActivities" :vectorId="moduleId" :moduleId="moduleId" />
              </div>
            </div>
          </div>

          <div class="ld-foot">
            <div class="ld-foot__note">GNey never overwrites a customized card — new fields are appended, redesigns arrive as suggestions.</div>
            <button class="ld-btn ld-btn--user" :disabled="!rows.length || applying" @click="saveCustom">Save</button>
          </div>
        </div>
      </template>
    </template>

    <div class="listing-designer-toggle" slot="reference">
      <slot><div :style="getToggleStyle"></div></slot>
    </div>
  </fts-popper>
</template>

<script>
import FTSPopper from './FTSPopperNew';
import FTSSpinner from './FTSSpinner';
import FTSAddRowDropdown from './FTSAddRowDropdown';
import FTSDefaultActionDropdown from './FTSDefaultActionDropdown';
import FTSDesignerRowMenuDropdown from './FTSDesignerRowMenuDropdown';
import FTSIconFieldDropdown from './FTSIconFieldDropdown';
import FTSAppListCard, { POSTER_LAYOUTS } from './FTSAppListCard';
import commons from '../commons/commons';
import { clientStore } from '../store/clientStore';

/* Field types AppListGuard accepts as the card icon (Image, Images, File, Files, Select). */
const ICON_TYPES = [10, 11, 12, 13, 27];

/* The two layouts every renderer knows, and the only two this control offers: 'detail' is an icon
   beside the rows, 'grid' is a photo-first tile. The web list is one column wide, so the app's own
   layouts below have nothing to say here — a card wearing one keeps it and shows it read-only. */
const LAYOUT_TYPES = [{ value: 'detail', label: 'Detail' }, { value: 'grid', label: 'Grid' }];
const APP_LAYOUT_LABELS = { '1column': '1 column', '2column': '2 column', masonry2column: 'Masonry' };
const GRID_SIZES = ['S', 'M', 'L'];
/* Field types that make a grid worth pinning: without one there is no photo to lead with. */
const IMAGE_TYPES = [10, 11];
const ICON_SIZES = ['XS', 'S', 'M', 'L'];

const SYSTEM_INFORMATIONS = [
  { id: 'state', displayName: 'State', system: true, fieldName: 'state', type: 'state' },
];

/* Pure state helpers, exported for the unit tests. */
export const listDesignerHelpers = {
  /* What the origin chip says, from the GET card description. */
  originLabel(design) {
    if (!design || !design.appList) return 'No card yet';
    const meta = design.meta || design.appList.meta;
    const when = meta && meta.generatedAt ? ' · ' + commons.momentRelativeTime(meta.generatedAt) : '';
    // A view without a card of its own renders the module design (R13); Customize starts from it.
    if (design.inherited || design.origin === 'inherited') return '↳ Inherited from Default' + when;
    if (design.origin === 'user') return '🛠 Customized' + when;
    // A card the module's author sent with the schema (MCP client or GNey; meta.model "author:…")
    // is an AI card too, but nobody pressed Generate for it.
    if (design.origin === 'ai' && meta && typeof meta.model === 'string' && meta.model.indexOf('author:') === 0)
      return '✦ Designed with the module' + when;
    if (design.origin === 'ai') return '✨ AI-designed' + when;
    if (design.origin === 'baseline') return 'Auto baseline' + when;
    return 'Configured on the app';
  },
  /* The card-level shell the Customize editor starts from, normalised so every control has a value
     to show: the layout it arrived on, a grid size (the app defaults new grids to M), the icon if
     the card carries one, and whether the Poster switch is on. */
  initialLayout(design) {
    const appList = (design && design.appList) || (design && design.backup) || null;
    return {
      type: (appList && appList.type) || 'detail',
      size: GRID_SIZES.indexOf(appList && appList.size) > -1 ? appList.size : 'M',
      icon: appList && appList.icon ? Object.assign({}, appList.icon) : null,
      overlay: !!(appList && appList.overlay === true),
    };
  },
  /* A candidate's shell in words. The candidate row used to print the bare type ("detail"), which
     says nothing about the two decisions that change the card most: how many columns a grid takes,
     and which field a detail card leads with. */
  describeShell(appList, informations) {
    if (!appList) return '';
    const type = appList.type;
    const poster = appList.overlay === true && POSTER_LAYOUTS.indexOf(type) > -1 ? ' · poster' : '';
    if (type === 'grid') return 'Grid' + (appList.size ? ' · ' + appList.size : '') + poster;
    if (poster) return (APP_LAYOUT_LABELS[type] || 'Detail') + poster;
    const label = APP_LAYOUT_LABELS[type] || 'Detail';
    const icon = appList.icon && appList.icon.informationId
      ? (informations || []).find((f) => f.id === appList.icon.informationId)
      : null;
    if (icon) return label + ' · ' + (icon.displayName || icon.name || icon.fieldName || icon.id);
    return label + ' · no icon';
  },
  /* True for a layout only the app can draw, so the picker shows it instead of offering it. */
  isAppLayout(type) {
    return Object.prototype.hasOwnProperty.call(APP_LAYOUT_LABELS, type);
  },
  /* An icon the guard will accept: an Image/Images/File/Files/Selection field that still exists.
     A card can point at a field since retyped or deleted — drop such an icon rather than let Save
     be rejected for something the person never chose. */
  validIcon(icon, informations) {
    if (!icon || !icon.informationId) return null;
    const field = (informations || []).find((f) => f.id === icon.informationId);
    if (!field || ICON_TYPES.indexOf(field.type) < 0) return null;
    return { informationId: icon.informationId, size: ICON_SIZES.indexOf(icon.size) > -1 ? icon.size : 'M' };
  },
  /* The mode the control opens in: a person's card opens on Customize, anything else on GNey. */
  initialMode(design) {
    return design && design.origin === 'user' ? 'custom' : 'ai';
  },
  /* The rows the Customize editor starts from: the card the person is looking at, whatever its
     origin — customizing a baseline or GNey card means tweaking it, not rebuilding it (Save is what
     stamps origin user). With no card yet, the parked backup; else empty. */
  initialRows(design) {
    if (!design) return [];
    if (design.appList && Array.isArray(design.appList.rows) && design.appList.rows.length) return cloneRows(design.appList.rows);
    if (design.backup && Array.isArray(design.backup.rows)) return cloneRows(design.backup.rows);
    return [];
  },
  /* The appList a hand-built card becomes: the edited layout shell with the edited rows and action.
     'size' belongs to a grid and 'icon' to a detail, and the guard refuses the pairing either way
     round; an app layout is exempt from that split, and keeps whatever it arrived carrying. */
  customAppList(design, rows, action, informations, layout) {
    const base = design && design.appList ? JSON.parse(JSON.stringify(design.appList)) : {};
    delete base.meta;
    delete base.ai;

    const shell = layout || listDesignerHelpers.initialLayout(design);
    const type = shell.type || 'detail';
    const icon = listDesignerHelpers.validIcon(shell.icon, informations);
    base.type = type;
    if (type === 'grid') {
      delete base.icon;
      base.size = GRID_SIZES.indexOf(shell.size) > -1 ? shell.size : 'M';
    } else {
      if (type === 'detail') delete base.size;
      if (icon) base.icon = icon; else delete base.icon;
    }
    // The Poster switch travels only on a layout that can draw the tile.
    if (shell.overlay && POSTER_LAYOUTS.indexOf(type) > -1) base.overlay = true;
    else delete base.overlay;

    // Rows the person did not touch keep their text size/style, image ratio/orientation and widget
    // settings; the row dropdown only knows {type, id}, so anything it re-emits starts plain.
    base.rows = cloneRows(rows).map((row) => ({
      items: row.items.map((item) => {
        const out = item.id === 'defaultCard' ? { type: 'widget', id: 'defaultCard' } : { type: item.type || 'information', id: item.id };
        ['size', 'style', 'ratio', 'orientation', 'settings'].forEach((k) => { if (item[k] !== undefined && item[k] !== null) out[k] = item[k]; });
        return out;
      }),
    }));
    base.action = action || base.action || 'view';
    return base;
  },
  /* Why Generate came back empty: the server reports each failed attempt with its reason
     (provider error, timeout, repairs exhausted) — show the first one rather than a generic shrug. */
  emptyGenerateError(res) {
    const failure = res && Array.isArray(res.failures) ? res.failures.find((f) => f && f.error) : null;
    if (failure) return `GNey could not produce a valid card (${failure.outcome || 'failed'}): ${failure.error}`;
    return 'GNey could not produce a valid card for this listing. Try a different hint.';
  },
  /* Previews are only honest at the width the list really gives a card (D11): the detail view's
     column is a fixed 325px, so a card there is ~298px, not the popover's 380px. */
  previewStyle(width) {
    const w = Math.round(Number(width) > 0 ? Number(width) : 298);
    return { width: Math.max(200, w) + 'px', maxWidth: '100%' };
  },
  /* A stand-in entry when the listing has no rows yet (D11: live first, synthetic fallback). */
  syntheticItem(informations) {
    const extension = {};
    (informations || []).forEach((f) => {
      if (f.system) return;
      switch (f.type) {
        case 0: case 16: case 19: extension[f.fieldName] = f.displayName; break;
        case 2: extension[f.fieldName] = 42; break;
        case 3: case 4: extension[f.fieldName] = 1234.5; break;
        case 5: case 6: extension[f.fieldName] = new Date().toISOString(); break;
        case 25: extension[f.fieldName] = 'name@example.com'; break;
        case 26: extension[f.fieldName] = '+60 12-345 6789'; break;
        case 27: extension[f.fieldName] = f.optionList && f.optionList[0] ? (f.optionList[0].displayName || f.optionList[0].name) : 'Option'; break;
        case 28: extension[f.fieldName] = f.optionList ? f.optionList.slice(0, 2).map((o) => o.displayName || o.name) : []; break;
        case 33: extension[f.fieldName] = 'example.com'; break;
        default: break;
      }
    });
    return {
      id: 'sample', documentId: 'SAMPLE-1', title: 'Sample entry', extension,
      audit: { createdBy: 'you', createdDate: new Date().toISOString(), updatedBy: 'you', updatedDate: new Date().toISOString() },
      state: null, assignees: [],
    };
  },
};

function cloneRows(rows) {
  return (rows || []).map((row) => ({ items: (row.items || []).map((item) => Object.assign({}, item)) }));
}

export default {
  name: 'ListingDesigner',
  components: {
    'fts-popper': FTSPopper,
    'fts-spinner': FTSSpinner,
    'fts-add-row-dropdown': FTSAddRowDropdown,
    'fts-default-action-dropdown': FTSDefaultActionDropdown,
    'fts-designer-row-menu-dropdown': FTSDesignerRowMenuDropdown,
    'fts-icon-field-dropdown': FTSIconFieldDropdown,
    'fts-app-list-card': FTSAppListCard,
  },
  props: {
    moduleId: String,
    listingId: String,
    moduleName: String,
    informations: { type: Array, default: () => [] },
    activities: { type: Array, default: () => [] },
    states: { type: Array, default: () => [] },
    sampleItems: { type: Array, default: () => [] },
    /* Width of a card in the list the design applies to, so previews match what people will see. */
    previewWidth: { type: Number, default: 298 },
  },
  data() {
    return {
      loading: false,
      error: null,
      design: null,
      schema: null,
      mode: 'ai',
      hint: '',
      generating: false,
      candidates: [],
      selectedKey: null,
      applying: false,
      rows: [],
      parkedRows: null,
      action: 'view',
      layout: { type: 'detail', size: 'M', icon: null },
      /* null = let GNey choose (the default); 'detail' / 'grid' pin the tool schema. */
      aiLayout: null,
      layoutTypes: LAYOUT_TYPES,
      gridSizes: GRID_SIZES,
      iconSizes: ICON_SIZES,
      defaultActivities: [{ id: 'view', name: 'View', 'fa-icon': 'file' }, { id: 'edit', name: 'Edit', 'fa-icon': 'pen-to-square' }],
      getToggleStyle: {},
    };
  },
  computed: {
    /* The module schema behind the editor and previews: from GET when the server sends it, else the props. */
    columns() {
      return this.schema ? this.schema.information : (this.informations || []);
    },
    allActivities() {
      return this.schema ? this.schema.activities : (this.activities || []);
    },
    allStates() {
      return this.schema && this.schema.states.length ? this.schema.states : (this.states || []);
    },
    previewStyle() {
      return listDesignerHelpers.previewStyle(this.previewWidth);
    },
    originKind() {
      return this.design && this.design.origin ? this.design.origin : 'none';
    },
    originLabel() {
      return listDesignerHelpers.originLabel(this.design);
    },
    selectedCandidate() {
      return this.candidates.find((c) => c.key === this.selectedKey) || null;
    },
    previewItem() {
      return (this.sampleItems && this.sampleItems[0]) || listDesignerHelpers.syntheticItem(this.columns);
    },
    editableInformations() {
      const fields = (this.columns || [])
        .filter((f) => f && f.id && !f.system && f.id !== 'state' && f.id !== 'defaultCard' && f.type !== 'widget')
        .map((f) => Object.assign({}, f, { displayName: f.displayName || f.name || f.fieldName || String(f.id) }));
      return [...SYSTEM_INFORMATIONS, ...fields];
    },
    listableActivities() {
      return (this.allActivities || [])
        .filter((a) => a && a.id && ['create', 'update', 'view', 'edit'].indexOf(String(a.name || '').toLowerCase()) < 0)
        .map((a) => Object.assign({}, a, { name: a.name || String(a.id) }));
    },
    customAppList() {
      return listDesignerHelpers.customAppList(this.design, this.rows, this.action, this.columns, this.layout);
    },
    isAppLayout() {
      return listDesignerHelpers.isAppLayout(this.layout.type);
    },
    hasImageField() {
      return (this.columns || []).some((f) => f && IMAGE_TYPES.indexOf(f.type) > -1);
    },
    layoutPins() {
      const pins = [{ value: null, label: 'Any' }, { value: 'detail', label: 'Detail' }];
      if (this.hasImageField) pins.push({ value: 'grid', label: 'Grid' });
      return pins;
    },
    appLayoutLabel() {
      return APP_LAYOUT_LABELS[this.layout.type] || this.layout.type;
    },
    /* The Poster switch needs a photo row to fill the tile and a layout that can take one. */
    posterAvailable() {
      return POSTER_LAYOUTS.indexOf(this.layout.type) > -1 && this.rows.some((row) => this.isImageCardRow(row));
    },
    /* Only fields the guard will take as an icon reach the picker. */
    iconFields() {
      return (this.columns || []).filter((f) => f && f.id && ICON_TYPES.indexOf(f.type) > -1);
    },
    iconFieldName() {
      const icon = this.layout.icon;
      if (!icon || !icon.informationId) return 'No icon';
      const field = this.iconFields.find((f) => f.id === icon.informationId);
      return field ? (field.displayName || field.name || field.fieldName || field.id) : 'No icon';
    },
  },
  methods: {
    async onShow() {
      await this.load();
    },
    async load() {
      if (!this.moduleId || !this.listingId) return;
      this.loading = true;
      this.error = null;
      this.candidates = [];
      this.selectedKey = null;
      try {
        const design = await clientStore.dispatch('loadListingCardDesign', { moduleId: this.moduleId, listingId: this.listingId });
        this.design = design;
        this.schema = design && Array.isArray(design.information) && design.information.length
          ? { information: design.information, activities: design.activities || [], states: design.states || [] }
          : null;
        this.mode = listDesignerHelpers.initialMode(design);
        this.rows = listDesignerHelpers.initialRows(design);
        this.layout = listDesignerHelpers.initialLayout(design);
        if (this.aiLayout === 'grid' && !this.hasImageField) this.aiLayout = null;
        this.parkedRows = null;
        this.action = (design && design.appList && design.appList.action) || 'view';
      } catch (err) {
        this.error = this.describeError(err, 'Could not load the card design');
      } finally {
        this.loading = false;
      }
    },
    describeShell(appList) {
      return listDesignerHelpers.describeShell(appList, this.columns);
    },
    setMode(mode) {
      if (mode === this.mode) return;
      if (mode === 'ai') {
        // Customize → GNey parks the manual rows so switching back loses nothing (D12).
        if (this.rows.length) this.parkedRows = cloneRows(this.rows);
      } else if (!this.rows.length) {
        this.rows = this.parkedRows ? cloneRows(this.parkedRows) : listDesignerHelpers.initialRows(this.design);
      }
      this.mode = mode;
    },
    async generate() {
      if (this.generating) return;
      this.generating = true;
      this.error = null;
      try {
        const res = await clientStore.dispatch('suggestListingCardDesign', {
          moduleId: this.moduleId, listingId: this.listingId, hint: this.hint, count: 3, layout: this.aiLayout,
        });
        this.candidates = (res && res.candidates) || [];
        this.selectedKey = this.candidates.length ? this.candidates[0].key : null;
        if (!this.candidates.length) this.error = listDesignerHelpers.emptyGenerateError(res);
      } catch (err) {
        this.error = this.describeError(err, 'GNey could not generate a card');
      } finally {
        this.generating = false;
      }
    },
    async applyCandidate() {
      if (!this.selectedCandidate) return;
      await this.apply(this.selectedCandidate.appList, 'ai');
    },
    async saveCustom() {
      await this.apply(this.customAppList, 'user');
    },
    async restoreBackup() {
      if (!this.design || !this.design.backup) return;
      const restored = JSON.parse(JSON.stringify(this.design.backup));
      delete restored.meta;
      await this.apply(restored, 'user');
    },
    async apply(appList, origin) {
      if (this.applying) return;
      this.applying = true;
      this.error = null;
      try {
        await clientStore.dispatch('applyListingCardDesign', { moduleId: this.moduleId, listingId: this.listingId, appList, origin });
        this.$emit('applied', { origin, appList });
        if (this.$refs.popper && typeof this.$refs.popper.doClose === 'function') this.$refs.popper.doClose();
      } catch (err) {
        this.error = this.describeError(err, 'The card could not be applied');
      } finally {
        this.applying = false;
      }
    },
    describeError(err, fallback) {
      const data = err && err.response && err.response.data;
      const message = (data && (data.message || data.error)) || (err && err.message);
      return message ? `${fallback}: ${message}` : fallback;
    },
    /* ---- Customize editor (kept from the prototype, bound to the real schema) ---- */
    onRowRearrange(index, operation) {
      if (index < 0 || index >= this.rows.length) return;
      const row = this.rows[index];
      this.rows.splice(index, 1);
      switch (operation) {
        case 'moveTop': this.rows.unshift(row); break;
        case 'moveBottom': this.rows.push(row); break;
        case 'moveUp': this.rows.splice(Math.max(index - 1, 0), 0, row); break;
        case 'moveDown': this.rows.splice(Math.min(index + 1, this.rows.length), 0, row); break;
        case 'removeRow': break;
        default: this.rows.splice(index, 0, row);
      }
    },
    onSelectAction(action) {
      this.action = action;
    },
    /* ---- Layout shell ---- */
    setLayoutType(type) {
      if (this.layout.type === type) return;
      // Give the newly relevant control something to show, the way the app's designer does: a fresh
      // grid starts at M, a fresh detail borrows the first icon-eligible field.
      const icon = this.layout.icon
        || (type === 'detail' && this.iconFields.length ? { informationId: this.iconFields[0].id, size: 'M' } : null);
      this.layout = Object.assign({}, this.layout, { type, icon });
    },
    setGridSize(size) {
      this.layout = Object.assign({}, this.layout, { size });
    },
    setIconField(informationId) {
      const icon = informationId
        ? { informationId, size: (this.layout.icon && this.layout.icon.size) || 'M' }
        : null;
      this.layout = Object.assign({}, this.layout, { icon });
    },
    setIconSize(size) {
      if (!this.layout.icon) return;
      this.layout = Object.assign({}, this.layout, { icon: Object.assign({}, this.layout.icon, { size }) });
    },
    setOverlay(on) {
      this.layout = Object.assign({}, this.layout, { overlay: !!on });
    },
    onSelectOption(selectedIds, index) {
      if (index === null && selectedIds.length > 0) {
        this.rows.push({ items: selectedIds });
      } else if (selectedIds.length === 0 && index !== null) {
        this.rows.splice(index, 1);
      } else if (index !== null) {
        this.rows.splice(index, 1, { items: selectedIds });
      }
    },
    isDefaultCardRow(row) {
      return Array.isArray(row.items) && row.items.length === 1 && row.items[0].id === 'defaultCard';
    },
    isImageCardRow(row) {
      if (!Array.isArray(row.items) || row.items.length !== 1) return false;
      const field = (this.columns || []).find((x) => x.id === row.items[0].id);
      return !!field && (field.type === 10 || field.type === 11);
    },
    cellClass(cell) {
      return ['ld-cell', cell.size ? `ld-cell--${String(cell.size).toLowerCase()}` : '', cell.style ? `ld-cell--style-${cell.style}` : ''];
    },
    getCellAlignClass(cellCount, index) {
      if (cellCount === 1) return 'align-left';
      if (cellCount === 2) return index === 0 ? 'align-left' : 'align-right';
      if (cellCount === 3) return index < 2 ? 'align-left' : 'align-right';
      return '';
    },
    getInvalidItems(i) {
      return this.rows.filter((_, index) => index !== i).flatMap((row) => row.items);
    },
    getActionName(id) {
      const activity = this.defaultActivities.find((x) => x.id === id) || (this.allActivities || []).find((x) => x.id === id);
      return activity ? activity.name : '';
    },
    getInformationName(item) {
      const information = this.editableInformations.find((x) => x.id === item.id);
      return information ? information.displayName : item.id;
    },
    getActivityName(item) {
      const activity = (this.allActivities || []).find((x) => x.id === item.id);
      return activity ? activity.name : item.id;
    },
  },
};
</script>

<style lang="scss">
.listing-designer-dropdown {
  width: 420px;
  background: var(--fts-white, #fff);
  border: 1px solid var(--fts-gray-300, #e0e0e0);
  border-radius: 12px;
  padding: 14px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.07), 0 3px 6px -4px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  gap: 12px;
  color: var(--fts-text, #333);
  font-size: 0.875rem;

  .ld-header { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
  .ld-header__title { display: flex; align-items: center; gap: 6px; font-weight: 500; }
  .ld-header__listing { color: var(--fts-default); font-weight: 400; }

  .ld-origin {
    font-size: 0.75rem; font-weight: 600; border-radius: 999px; padding: 2px 9px; white-space: nowrap;
    background: var(--fts-gray-100, #f6f6f6); color: var(--fts-default);
    &.ld-origin--ai { color: var(--fts-ai); background: rgba(79, 70, 229, 0.10); }
    &.ld-origin--user { color: #6941c6; background: rgba(105, 65, 198, 0.10); }
    &.ld-origin--inherited { color: #475467; background: rgba(71, 84, 103, 0.10); }
  }

  .ld-loading { display: flex; align-items: center; gap: 8px; color: var(--fts-default); }
  .ld-error { color: var(--fts-danger, #d50000); font-size: 0.8125rem; }

  .ld-seg {
    display: flex; gap: 3px; padding: 3px; border-radius: 8px;
    background: var(--fts-gray-100, #f6f6f6); border: 1px solid var(--fts-gray-300, #e0e0e0);
  }
  .ld-seg__item {
    flex: 1; text-align: center; padding: 5px 8px; border-radius: 6px; cursor: pointer; font-weight: 500; color: var(--fts-default);
    display: flex; align-items: center; justify-content: center; gap: 6px;
    transition: background 120ms ease, color 120ms ease;
    &.ld-seg__item--on { background: var(--fts-white, #fff); box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12); }
    &.ld-seg__item--ai { color: var(--fts-ai); }
    &.ld-seg__item--user { color: #6941c6; }
  }

  .ld-section { display: flex; flex-direction: column; gap: 10px; }

  .ld-pin { display: flex; align-items: center; gap: 8px; font-size: 0.8125rem; color: var(--fts-default); }
  .ld-pin__label { color: var(--fts-text); }

  .ld-hint { display: flex; gap: 8px; }
  .ld-hint__input {
    flex: 1; min-width: 0; border: 1px solid var(--fts-gray-300, #e0e0e0); border-radius: 4px; padding: 6px 10px; font-size: 0.8125rem;
    &:focus { outline: none; box-shadow: inset 0 0 0 1px var(--fts-primary, #2b9ec6); border-color: var(--fts-primary, #2b9ec6); }
  }

  .ld-btn {
    border: none; border-radius: 4px; padding: 6px 14px; font-weight: 500; color: #fff; cursor: pointer; white-space: nowrap;
    display: inline-flex; align-items: center; gap: 6px; transition: transform 120ms ease, opacity 120ms ease;
    &:disabled { opacity: 0.45; cursor: default; }
    &:not(:disabled):active { transform: scale(0.98); }
    &.ld-btn--ai { background: var(--fts-ai); }
    &.ld-btn--user { background: #6941c6; }
  }

  .ld-candidates { display: flex; flex-direction: column; gap: 8px; }
  .ld-candidate {
    border: 1.5px solid var(--fts-gray-300, #e0e0e0); border-radius: 12px; padding: 8px 10px; cursor: pointer;
    background: var(--fts-gray-100, #f6f6f6);
    &.ld-candidate--selected { border-color: var(--fts-ai); box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.12); background: var(--fts-white, #fff); }
  }
  .ld-candidate__pick { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; font-size: 0.75rem; color: var(--fts-default); }
  .ld-candidate__key { font-weight: 700; color: var(--fts-text); }
  .ld-radio {
    width: 12px; height: 12px; border-radius: 50%; border: 1.5px solid var(--fts-gray-300, #e0e0e0); background: var(--fts-white, #fff);
    &.ld-radio--on { border-color: var(--fts-ai); background: var(--fts-ai); box-shadow: inset 0 0 0 2.5px var(--fts-white, #fff); }
  }
  .ld-candidate__preview { pointer-events: none; }
  .ld-preview__card { box-sizing: border-box; }

  .ld-empty { color: var(--fts-default); font-size: 0.8125rem; padding: 4px 0; }

  .ld-preview { border: 1px dashed var(--fts-gray-300, #e0e0e0); border-radius: 12px; padding: 8px 10px; }
  .ld-preview__cap { font-size: 0.6875rem; letter-spacing: 0.08em; text-transform: uppercase; color: var(--fts-default); margin-bottom: 6px; }

  .ld-foot { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-top: 4px; }
  .ld-foot__note { font-size: 0.6875rem; color: var(--fts-default); line-height: 1.4; max-width: 60%; }

  /* Customize editor (from the prototype) */
  .card-section-wrapper { width: 100%; border: 1px solid var(--fts-gray-300, #e0e0e0); border-radius: 12px; padding: 0.4rem; background: var(--fts-gray-100, #f6f6f6); }
  .card-column { width: 100%; display: flex; flex-direction: column; gap: 0.2rem; }
  .card-row { display: flex; align-items: center; background: var(--fts-white, #fff); border: 1px solid var(--fts-gray-300, #e0e0e0); border-radius: 4px; padding: 0.3rem 0.5rem; font-size: 0.8125rem; }
  .card-row.default-card { min-height: 40px; background: var(--fts-gray-100, #f6f6f6); }
  .row-cell { flex: 1; padding: 0 0.25rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .row-cell.align-center { text-align: center; }
  .row-cell.align-left { text-align: left; }
  .row-cell.align-right { text-align: right; }
  .ld-cell--s { font-size: 0.75rem; } .ld-cell--l { font-size: 1rem; }
  .ld-cell--style-b { font-weight: 700; } .ld-cell--style-l { font-weight: 300; }
  .activity-cell { border: 1px solid var(--fts-primary); padding: 2px 6px; border-radius: 4px; width: fit-content; }
  .drag-icon { flex: 0 0 auto; cursor: grab; color: var(--fts-default); padding-right: 0.5rem; }
  .add-row-wrapper { display: flex; justify-content: center; margin-top: 0.4rem; }
  .add-row-button { padding: 0.2rem 0.6rem; font-size: 0.8125rem; border-radius: 999px; border: 1px solid var(--fts-gray-300, #e0e0e0); background: var(--fts-white, #fff); cursor: pointer; color: var(--fts-text); }
  .settings-item { display: flex; justify-content: space-between; align-items: center; gap: 8px; padding: 0.5rem 0.75rem; border: 1px solid var(--fts-gray-300, #e0e0e0); border-radius: 4px; background: var(--fts-gray-100, #f6f6f6); font-size: 0.8125rem; }

  /* Layout shell: the same ledger row as Default action, with its control trailing right. */
  .ld-settings { display: flex; flex-direction: column; gap: 0.3rem; }
  .ld-choice { display: flex; gap: 3px; margin-left: auto; }
  .ld-choice__opt {
    min-width: 30px; padding: 2px 9px; font-size: 0.75rem; line-height: 1.5; cursor: pointer; color: var(--fts-default);
    border: 1px solid var(--fts-gray-300, #e0e0e0); border-radius: 4px; background: var(--fts-white, #fff);
    &.ld-choice__opt--on { border-color: var(--fts-primary, #2b9ec6); color: var(--fts-primary, #2b9ec6); font-weight: 500; }
    &:disabled { opacity: 0.45; cursor: default; }
  }
  .ld-poster__hint { color: var(--fts-default); font-size: 0.75rem; white-space: nowrap; }
  .ld-app-layout { margin-left: auto; color: var(--fts-text); }
  .ld-app-layout__note { color: var(--fts-default); }
  .edit-button { background: none; border: none; color: var(--fts-form-label); cursor: pointer; padding: 0; margin-left: auto; }
}
</style>
